<script setup>
import { ELearning } from '@pfsc/core/models/e-learning';
import { LICENSE_STATUS } from '@pfsc/core/models/license-status';
import { eLearningService } from '@pfsc/core/services/e-learning.service';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { useCoursesRequireDobStore } from '@pfsc/core/stores/courses-require-dob.store';
import { useTriggerStore } from '@pfsc/core/stores/trigger.store';
import { confirmDialogSerVice } from '@pfsc/core/ui-services/confirm-dialog.service';
import { downloadFileService } from '@pfsc/core/ui-services/download-file.service';
import { notificationService, NotificationType } from '@pfsc/core/ui-services/notification.service';
import { useDobDialog } from '@pfsc/core/ui-services/use-dob-dialog';
import { toggleExecutionState } from '@pfsc/core/utils/toggleExecutionState';
import { toReadableIfNull } from '@pfsc/core/utils/toReadableIfNull';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { computed, ref } from 'vue';
import TransferCourseFlowDialogs from '../transfer-course-dialog/transfer-course-flow-dialogs.vue';

const props = defineProps({
  eLearning: {
    type: ELearning,
    required: true,
  },
});

const { currentUser } = useAuthStore();
const { isCourseRequireDob } = useCoursesRequireDobStore();
const { showDobDialogWithConfig, closeDobDialog } = useDobDialog();
const trigger = useTriggerStore();

const isDownloadingCertificate = ref(false);
const isReturningLicense = ref(false);
const transferCourseDialogRef = ref(null);

const isCourseRequiredDateOfBirth = computed(
  () => isCourseRequireDob(props.eLearning.courseInfo.course),
);
const shouldShowDobDialog = computed(() => isCourseRequiredDateOfBirth.value && props.eLearning.user.dob == null);
const shouldShowTransfer = computed(() => props.eLearning.courseInfo?.isAllowTransfer && !currentUser.isStudent
  && props.eLearning.status.name !== LICENSE_STATUS.CANCELLED);
const shouldShowDownloadCert = computed(() => props.eLearning.courseInfo?.isCompleted && !props.eLearning.courseInfo?.isCertificateExpired
  && props.eLearning.status.name !== LICENSE_STATUS.CANCELLED);
const shouldShowReturnLicense = computed(() => shouldShowTransfer.value && currentUser.isPfsAdmin);

/** Handle transfer click. */
function onTransferClick() {
  transferCourseDialogRef.value.openDialog({
    ...props.eLearning.courseInfo,
    package: props.eLearning.package,
    registration: props.eLearning,
  }, props.eLearning.user);
}

/**
 * Notify the return license error.
 * @param {Error} e Error.
 */
function notifyReturnLicenseError(e) {
  notificationService.notify({ message: e?.message, type: NotificationType.error });
}

/** Handle return license click. */
function onReturnLicenseClick() {
  confirmDialogSerVice.showConfirmDialogWith({
    title: globalTranslate('returnLicenseDialog.title'),
    content: globalTranslate('returnLicenseDialog.content', {
      learner: props.eLearning.user.fullName,
      course: props.eLearning.courseInfo?.course?.name,
    }),
    confirmButtonText: globalTranslate('returnLicenseDialog.confirmButtonText'),
    cancelButtonText: globalTranslate('returnLicenseDialog.cancelButtonText'),
    width: 800,
    shouldCenterTitle: true,
    onConfirmClick: () => {
      toggleExecutionState(isReturningLicense, async () => {
        await eLearningService.returnLicense(props.eLearning.id);
        trigger.triggerRegistrationsChange();
        notificationService.notifyCommonSuccess();
      }, notifyReturnLicenseError);
    },
  });
}

/** Download certificate. */
function onDownloadCertificateClick() {
  toggleExecutionState(isDownloadingCertificate, async () => {
    const urls = await eLearningService.downloadCertificates([props.eLearning.id]);
    const downloadRequests = urls.map(url => downloadFileService.downloadDirectlyViaUrl(url));
    await Promise.all(downloadRequests);
    notificationService.notifyCommonSuccess();
  }, e => {
    const errorMessage = e?.message;
    if (shouldShowDobDialog.value) {
      showDobDialogWithConfig({
        dob: null,
        error: errorMessage,
        userId: props.eLearning.user.id,
        onDobSubmitted: () => {
          closeDobDialog();
          onDownloadCertificateClick();
        },
      });
    }
  });
}

</script>

<template>
  <div v-if="shouldShowTransfer">
    <div :class="$style.actions">
      <button
        type="button"
        class="text"
        @click="onTransferClick"
      >
        {{ globalTranslate("transferCourseDialog.transfer") }}
      </button>
      <button
        v-if="shouldShowReturnLicense"
        type="button"
        class="text"
        @click="onReturnLicenseClick"
      >
        {{ globalTranslate("returnLicenseDialog.return") }}
      </button>
    </div>
    <transfer-course-flow-dialogs ref="transferCourseDialogRef" />
  </div>
  <template v-else-if="shouldShowDownloadCert">
    <button
      type="button"
      class="text"
      @click="onDownloadCertificateClick"
    >
      {{ globalTranslate("button.downloadCert") }}
    </button>
  </template>
  <p v-else>
    {{ toReadableIfNull(null) }}
  </p>
</template>

<style module>
.actions {
  gap: 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

@media (max-width: 600px) {
  .actions {
    gap: 0px;
  }
}
</style>
